import {
    AssignmentDeleteValidation,
    MatterAssignmentCreateDto,
    MatterAssignmentDto,
    MatterAssignmentMinDto,
    MatterAssignmentUpdateDto,
} from 'types/assignments';
import { portalAPI } from './portalAPI';
import { APITimecardsSlice } from './timecards';
import { accumulatedQueryWithFilters } from './util/endpointBuilder';

const APIAssignmentSlice = portalAPI
    .enhanceEndpoints({ addTagTypes: ['Assignment', 'AssignmentMin'] })
    .injectEndpoints({
        endpoints: (builder) => ({
            getMinAssignmentsListWithFilters: accumulatedQueryWithFilters<MatterAssignmentMinDto>(
                builder,
                '/v1/matter-assignment/min',
                {
                    providesTags: ['AssignmentMin'],
                }
            ),
            getAssignementsWithFilters: accumulatedQueryWithFilters<MatterAssignmentDto>(
                builder,
                '/v1/matter-assignment',
                {
                    providesTags: ['Assignment'],
                }
            ),
            addMatterAssignment: builder.mutation<MatterAssignmentDto, MatterAssignmentCreateDto>({
                query: ({ ...body }) => ({
                    url: `/v1/matter-assignment`,
                    method: 'POST',
                    body,
                }),
                invalidatesTags: ['Assignment', 'AssignmentMin'],
                async onQueryStarted(_, { dispatch, queryFulfilled }) {
                    await queryFulfilled;
                    dispatch(APITimecardsSlice.util.invalidateTags(['Timecards']));
                },
            }),
            updateMatterAssignment: builder.mutation<MatterAssignmentDto, MatterAssignmentUpdateDto>({
                query: ({ id, ...body }) => ({
                    url: `/v1/matter-assignment/${id}`,
                    method: 'PUT',
                    body,
                }),
                invalidatesTags: (_1, _2, arg) =>
                    arg.id
                        ? ['Assignment', { type: 'Assignment', id: arg.id }, 'AssignmentMin']
                        : ['Assignment', 'AssignmentMin'],
                async onQueryStarted(_, { dispatch, queryFulfilled }) {
                    await queryFulfilled;
                    dispatch(APITimecardsSlice.util.invalidateTags(['Timecards']));
                },
            }),
            deleteMatterAssignment: builder.mutation<boolean, { id: number }>({
                query: ({ id }) => ({
                    url: `/v1/matter-assignment/${id}`,
                    method: 'DELETE',
                }),
                invalidatesTags: (_1, _2, arg) =>
                    arg.id
                        ? ['Assignment', { type: 'Assignment', id: arg.id }, 'AssignmentMin']
                        : ['Assignment', 'AssignmentMin'],
                async onQueryStarted(_, { dispatch, queryFulfilled }) {
                    await queryFulfilled;
                    dispatch(APITimecardsSlice.util.invalidateTags(['Timecards']));
                },
            }),
            getMatterAssignmentDeleteValidation: builder.query<AssignmentDeleteValidation, { id: number }>({
                query: ({ id }) => `/v1/matter-assignment/${id}/delete-validation`,
                keepUnusedDataFor: 0,
            }),
        }),
        overrideExisting: false,
    });

export const {
    useGetMinAssignmentsListWithFiltersQuery,
    useLazyGetMinAssignmentsListWithFiltersQuery,
    useLazyGetAssignementsWithFiltersQuery,
    useAddMatterAssignmentMutation,
    useUpdateMatterAssignmentMutation,
    useDeleteMatterAssignmentMutation,
    useLazyGetMatterAssignmentDeleteValidationQuery,
} = APIAssignmentSlice;
